import { AuthorSignature } from '@/components/AuthorSignature';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Play, Square, Volume2, VolumeX, Trash2, Save, RotateCcw, Plus, Minus,
  Hand, PowerOff, Power, Music2, Layers, Share2, Activity, Crosshair,
} from 'lucide-react';

import { Slider } from './ui/slider';
import { Input } from './ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Toaster, toast } from './ui/sonner';

import { getEngine } from '../lib/engines';
import { TIME_SIGNATURES, findSig } from '../lib/timeSignatures';
import { SUBDIVISIONS, SOUND_TYPES, KIT_PRESETS } from '../lib/options';
import useWakeLock from '../lib/useWakeLock';
import { buildShareUrl, readPresetFromUrl, clearPresetFromUrl } from '../lib/shareLink';
import { BeatPatternRow, BarGrid } from './BeatGrid';
import SongPanel from './SongPanel';
import InstallPrompt from './InstallPrompt';
import PracticePanel from './PracticePanel';
import ShareImportBanner from './ShareImportBanner';
import PolyrhythmPanel from './PolyrhythmPanel';
import QRShareModal from './QRShareModal';
import TopNav from './TopNav';
import { useMidiTransport } from '@/hooks/useMidi';

const STORAGE_KEY_PRESETS = 'metronomo:presets:v2';
const STORAGE_KEY_LAST = 'metronomo:last:v3';

const MIN_BPM = 30;
const MAX_BPM = 300;
const MAX_BARS = 1000;

const clampBpm = (n) => Math.max(MIN_BPM, Math.min(MAX_BPM, Math.round(n) || MIN_BPM));
const clampBars = (n) => Math.max(1, Math.min(MAX_BARS, Math.round(n) || 1));

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function loadLast() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_LAST);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}
function loadPresets() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PRESETS);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}
function savePresets(list) {
  localStorage.setItem(STORAGE_KEY_PRESETS, JSON.stringify(list));
}

export default function Metronome() {
  const last = useMemo(() => loadLast(), []);

  // Common state
  const [bpm, setBpm] = useState(last?.bpm ?? 120);
  const [bpmInput, setBpmInput] = useState(String(last?.bpm ?? 120));
  const [signature, setSignature] = useState(last?.signature ?? '4/4');
  const [pattern, setPattern] = useState(last?.pattern ?? findSig(last?.signature ?? '4/4').pattern);
  const [barsEnabled, setBarsEnabled] = useState(last?.barsEnabled ?? Array(4).fill(true));
  const [volume, setVolume] = useState(last?.volume ?? 0.7);
  const [muted, setMuted] = useState(false);
  const [subdivision, setSubdivision] = useState(last?.subdivision ?? 1);
  const [soundType, setSoundType] = useState(last?.soundType ?? 'square');
  const [kitMode, setKitMode] = useState(last?.kitMode ?? false);
  const [soundClick, setSoundClick] = useState(last?.soundClick ?? 'hihat');
  const [soundBeat, setSoundBeat] = useState(last?.soundBeat ?? 'snare');
  const [soundHigh, setSoundHigh] = useState(last?.soundHigh ?? 'kick');

  // Mode state
  const [mode, setMode] = useState(last?.mode ?? 'single');
  const [sections, setSections] = useState(last?.sections ?? []);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const [loopSong, setLoopSong] = useState(last?.loopSong !== false);

  // Practice tools
  const [countInBars, setCountInBars] = useState(last?.countInBars ?? 0);
  const [rampEnabled, setRampEnabled] = useState(last?.rampEnabled ?? false);
  const [rampDelta, setRampDelta] = useState(last?.rampDelta ?? 2);
  const [rampEveryBars, setRampEveryBars] = useState(last?.rampEveryBars ?? 4);
  const [rampMaxBpm, setRampMaxBpm] = useState(last?.rampMaxBpm ?? MAX_BPM);
  const [practiceCollapsed, setPracticeCollapsed] = useState(true);
  const [effectiveBpm, setEffectiveBpm] = useState(last?.bpm ?? 120);
  const [inCountIn, setInCountIn] = useState(false);
  const [countInBarsLeft, setCountInBarsLeft] = useState(0);

  // Polyrhythm voice B
  const [patternB, setPatternB] = useState(last?.patternB ?? [2, 1, 1]);
  const [soundTypeB, setSoundTypeB] = useState(last?.soundTypeB ?? 'sine');
  const [currentBeatB, setCurrentBeatB] = useState(-1);
  const [totalBarsPlayed, setTotalBarsPlayed] = useState(-1);

  // Rhythm Trainer (poly only)
  const [trainerEnabled, setTrainerEnabled] = useState(last?.trainerEnabled ?? false);
  const [trainerPlayBars, setTrainerPlayBars] = useState(last?.trainerPlayBars ?? 2);
  const [trainerMuteBars, setTrainerMuteBars] = useState(last?.trainerMuteBars ?? 2);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(() => getEngine('player').isRunning());
  const [currentBeat, setCurrentBeat] = useState(-1);
  const [currentBar, setCurrentBar] = useState(-1);
  const [currentSection, setCurrentSection] = useState(-1);

  // Presets
  const [presets, setPresets] = useState(() => loadPresets());
  const [presetName, setPresetName] = useState('');

  // Import-from-URL
  const [importedPreset, setImportedPreset] = useState(null);

  // Share modal
  const [shareModal, setShareModal] = useState(null); // { url, title, subtitle } | null

  const engineRef = useRef(null);
  const tapTimesRef = useRef([]);
  const tapBtnRef = useRef(null);

  // Keep the screen awake while playing — critical for mobile metronome use
  useWakeLock(isPlaying);

  if (!engineRef.current) {
    engineRef.current = getEngine('player');
    engineRef.current.configuredByPage = true;
  }

  // ===== Engine wiring =====
  useEffect(() => { engineRef.current.setBpm(bpm); }, [bpm]);
  useEffect(() => { engineRef.current.setPattern(pattern); }, [pattern]);
  useEffect(() => { engineRef.current.setBarsEnabled(barsEnabled); }, [barsEnabled]);
  useEffect(() => { engineRef.current.setSubdivision(subdivision); }, [subdivision]);
  useEffect(() => { engineRef.current.setSoundType(soundType); }, [soundType]);
  useEffect(() => {
    const e = engineRef.current;
    if (kitMode) {
      e.setSoundTypeForState(1, soundClick);
      e.setSoundTypeForState(2, soundBeat);
      e.setSoundTypeForState(3, soundHigh);
    } else {
      e.setSoundTypeForState(1, null);
      e.setSoundTypeForState(2, null);
      e.setSoundTypeForState(3, null);
    }
  }, [kitMode, soundClick, soundBeat, soundHigh]);
  useEffect(() => { engineRef.current.setMode(mode); }, [mode]);
  useEffect(() => { engineRef.current.setSections(sections); }, [sections]);
  useEffect(() => { engineRef.current.setLoopSong(loopSong); }, [loopSong]);
  useEffect(() => { engineRef.current.setVolume(muted ? 0 : volume); }, [volume, muted]);
  useEffect(() => { engineRef.current.setCountInBars(countInBars); }, [countInBars]);
  useEffect(() => { engineRef.current.setRampEnabled(rampEnabled); }, [rampEnabled]);
  useEffect(() => { engineRef.current.setRampDelta(rampDelta); }, [rampDelta]);
  useEffect(() => { engineRef.current.setRampEveryBars(rampEveryBars); }, [rampEveryBars]);
  useEffect(() => { engineRef.current.setRampMaxBpm(rampMaxBpm); }, [rampMaxBpm]);
  useEffect(() => { engineRef.current.setPatternB(patternB); }, [patternB]);
  useEffect(() => { engineRef.current.setSoundTypeB(soundTypeB); }, [soundTypeB]);
  useEffect(() => { engineRef.current.setTrainerEnabled(trainerEnabled); }, [trainerEnabled]);
  useEffect(() => { engineRef.current.setTrainerPlayBars(trainerPlayBars); }, [trainerPlayBars]);
  useEffect(() => { engineRef.current.setTrainerMuteBars(trainerMuteBars); }, [trainerMuteBars]);

  useEffect(() => {
    const e = engineRef.current;
    e.onBeat = (info) => {
      setCurrentBar(info.bar);
      setCurrentBeat(info.beatInBar);
      setCurrentSection(info.section);
      setInCountIn(!!info.isCountIn);
      setCountInBarsLeft(info.countInBarsLeft || 0);
      if (typeof info.totalBars === 'number') setTotalBarsPlayed(info.totalBars);
    };
    e.onSongEnd = () => {
      setIsPlaying(false);
      setCurrentBeat(-1);
      setCurrentBar(-1);
      setCurrentSection(-1);
      setInCountIn(false);
      setEffectiveBpm(bpm);
      toast('Song completata');
    };
    e.onCountInEnd = () => {
      setInCountIn(false);
      setCountInBarsLeft(0);
    };
    e.onBpmRamp = (newBpm) => {
      setEffectiveBpm(newBpm);
    };
    e.onBeatB = (info) => {
      setCurrentBeatB(info.beatIndex);
    };
    return () => {
      e.onBeat = null; e.onSongEnd = null; e.onCountInEnd = null; e.onBpmRamp = null; e.onBeatB = null;
    };
  }, [bpm]);

  // Persist last config
  useEffect(() => {
    const data = {
      bpm, signature, pattern, barsEnabled, volume,
      subdivision, soundType, mode, sections, loopSong,
      countInBars, rampEnabled, rampDelta, rampEveryBars, rampMaxBpm,
      patternB, soundTypeB,
      trainerEnabled, trainerPlayBars, trainerMuteBars,
      kitMode, soundClick, soundBeat, soundHigh,
    };
    try { localStorage.setItem(STORAGE_KEY_LAST, JSON.stringify(data)); } catch { /* ignore */ }
  }, [bpm, signature, pattern, barsEnabled, volume, subdivision, soundType, mode, sections, loopSong,
      countInBars, rampEnabled, rampDelta, rampEveryBars, rampMaxBpm, patternB, soundTypeB,
      kitMode, soundClick, soundBeat, soundHigh,
      trainerEnabled, trainerPlayBars, trainerMuteBars]);

  // Keeps playing after leaving the page; resync UI when stopped from elsewhere (bottom bar, lock screen).
  useEffect(() => engineRef.current.onRunning((running) => {
    setIsPlaying(running);
    if (!running) { setCurrentBeat(-1); setCurrentBar(-1); setCurrentSection(-1); setInCountIn(false); }
  }), []);

  // Detect ?p= shared preset on mount
  useEffect(() => {
    const p = readPresetFromUrl();
    if (p) {
      setImportedPreset(p);
      // Ensure user sees the banner even if they were scrolled
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }, []);

  // ===== Transport =====
  const togglePlay = useCallback(() => {
    const e = engineRef.current;
    if (e.isRunning()) {
      e.stop();
      setIsPlaying(false);
      setCurrentBeat(-1);
      setCurrentBar(-1);
      setCurrentSection(-1);
      setInCountIn(false);
      setEffectiveBpm(bpm);
    } else {
      if (mode === 'song' && sections.length === 0) {
        toast.error('Nessuna sezione da riprodurre');
        return;
      }
      setEffectiveBpm(bpm);
      e.start();
      setIsPlaying(true);
    }
  }, [mode, sections.length, bpm]);

  useEffect(() => {
    const onKey = (ev) => {
      if (ev.code === 'Space' && ev.target.tagName !== 'INPUT' && ev.target.tagName !== 'TEXTAREA') {
        ev.preventDefault();
        togglePlay();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePlay]);

  // (Auto-scroll to current bar disabled — user can scroll freely during playback)

  // ===== BPM handlers =====
  const applyBpm = (v) => {
    const c = clampBpm(v);
    setBpm(c);
    setBpmInput(String(c));
    if (mode === 'song' && sections[selectedSectionIndex]) {
      updateSection(selectedSectionIndex, { bpm: c });
    }
  };
  const handleBpmSlider = (vals) => applyBpm(vals[0]);
  const handleBpmInputCommit = () => applyBpm(parseInt(bpmInput, 10));
  const adjustBpm = (delta) => applyBpm(bpm + delta);
  useMidiTransport({ isPlaying, togglePlay, setBpm: applyBpm, getBpm: () => engineRef.current.effectiveBpm() });

  // ===== Pattern / signature =====
  const handleSignatureChange = (id) => {
    setSignature(id);
    const sig = findSig(id);
    setPattern(sig.pattern.slice());
    if (mode === 'song' && sections[selectedSectionIndex]) {
      updateSection(selectedSectionIndex, { signature: id, pattern: sig.pattern.slice() });
    }
  };

  const handleNumBarsChange = (n) => {
    const target = clampBars(n);
    setBarsEnabled((prev) => {
      if (target === prev.length) return prev;
      if (target > prev.length) return [...prev, ...Array(target - prev.length).fill(true)];
      return prev.slice(0, target);
    });
  };

  const cycleBeat = useCallback((index) => {
    setPattern((prev) => {
      const next = prev.slice();
      next[index] = (next[index] + 1) % 4;
      // mirror to active song section
      setSections((secs) => {
        if (mode !== 'song' || !secs[selectedSectionIndex]) return secs;
        const copy = secs.slice();
        copy[selectedSectionIndex] = { ...copy[selectedSectionIndex], pattern: next.slice() };
        return copy;
      });
      return next;
    });
  }, [mode, selectedSectionIndex]);

  const toggleBar = useCallback((index) => {
    setBarsEnabled((prev) => {
      const next = prev.slice();
      next[index] = !next[index];
      return next;
    });
  }, []);

  const allBeatsOff = () => setPatternAndMirror(pattern.map(() => 0), 'Tutti i quarti silenziati');
  const allBeatsOn = () => setPatternAndMirror(pattern.map((_, i) => (i === 0 ? 2 : 1)), 'Tutti i quarti attivati', true);
  const resetAccents = () => {
    if (pattern.length === 0) return;
    const next = pattern.map(() => 1);
    next[0] = 2;
    setPatternAndMirror(next, 'Accenti reimpostati', true);
  };

  function setPatternAndMirror(next, msg, success = false) {
    setPattern(next);
    if (mode === 'song' && sections[selectedSectionIndex]) {
      updateSection(selectedSectionIndex, { pattern: next });
    }
    success ? toast.success(msg) : toast(msg);
  }

  const allBarsOn = () => { setBarsEnabled((prev) => prev.map(() => true)); toast.success('Tutte le battute attive'); };
  const allBarsOff = () => { setBarsEnabled((prev) => prev.map(() => false)); toast('Tutte le battute disattivate'); };

  // ===== Tap tempo =====
  const handleTap = () => {
    const now = performance.now();
    const arr = tapTimesRef.current;
    const filtered = arr.filter((t) => now - t < 2000);
    filtered.push(now);
    tapTimesRef.current = filtered.slice(-5);

    if (tapBtnRef.current) {
      tapBtnRef.current.classList.remove('tap-flash');
      void tapBtnRef.current.offsetWidth;
      tapBtnRef.current.classList.add('tap-flash');
    }
    if (tapTimesRef.current.length >= 2) {
      const taps = tapTimesRef.current;
      const intervals = [];
      for (let i = 1; i < taps.length; i++) intervals.push(taps[i] - taps[i - 1]);
      const avgMs = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      applyBpm(60000 / avgMs);
    }
  };

  // ===== Sections management =====
  const updateSection = (index, changes) => {
    setSections((prev) => {
      const next = prev.slice();
      if (!next[index]) return prev;
      next[index] = { ...next[index], ...changes };
      return next;
    });
  };

  const addSection = () => {
    const newSection = {
      id: uid(),
      name: `Sezione ${sections.length + 1}`,
      bpm,
      signature,
      pattern: pattern.slice(),
      numBars: Math.min(barsEnabled.length, 64),
    };
    setSections((prev) => [...prev, newSection]);
    setSelectedSectionIndex(sections.length);
    toast.success(`"${newSection.name}" aggiunta`);
  };

  const duplicateSection = (i) => {
    setSections((prev) => {
      const sec = prev[i];
      if (!sec) return prev;
      const copy = { ...sec, id: uid(), name: `${sec.name} (copia)`, pattern: sec.pattern.slice() };
      const next = prev.slice();
      next.splice(i + 1, 0, copy);
      return next;
    });
    toast.success('Sezione duplicata');
  };

  const deleteSection = (i) => {
    setSections((prev) => prev.filter((_, idx) => idx !== i));
    setSelectedSectionIndex((prev) => Math.max(0, Math.min(prev, sections.length - 2)));
    toast('Sezione eliminata');
  };

  const moveSection = (i, delta) => {
    setSections((prev) => {
      const j = i + delta;
      if (j < 0 || j >= prev.length) return prev;
      const next = prev.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    if (selectedSectionIndex === i) setSelectedSectionIndex(i + delta);
  };

  const selectSection = (i) => {
    setSelectedSectionIndex(i);
    const sec = sections[i];
    if (!sec) return;
    setBpm(sec.bpm);
    setBpmInput(String(sec.bpm));
    setSignature(sec.signature);
    setPattern(sec.pattern.slice());
  };

  // When entering song mode with no sections, prompt to add
  const handleModeChange = (newMode) => {
    setMode(newMode);
    if (newMode === 'song' && sections.length === 0) {
      // auto-create initial section from current state
      const newSection = {
        id: uid(),
        name: 'Sezione 1',
        bpm,
        signature,
        pattern: pattern.slice(),
        numBars: Math.min(barsEnabled.length, 64),
      };
      setSections([newSection]);
      setSelectedSectionIndex(0);
    }
    if (engineRef.current.isRunning()) {
      engineRef.current.stop();
      setIsPlaying(false);
      setCurrentBeat(-1);
      setCurrentBar(-1);
      setCurrentSection(-1);
    }
  };

  // Polyrhythm handlers
  const cycleBeatB = useCallback((index) => {
    setPatternB((prev) => {
      const next = prev.slice();
      next[index] = (next[index] + 1) % 3;
      return next;
    });
  }, []);

  const handleSetBeatsB = (n) => {
    const target = Math.max(1, Math.min(16, n));
    setPatternB((prev) => {
      if (target === prev.length) return prev;
      if (target > prev.length) {
        const ext = prev.slice();
        while (ext.length < target) ext.push(1);
        return ext;
      }
      return prev.slice(0, target);
    });
  };

  const handleSetPolyPreset = (a, b) => {
    const sig = `${a}/4`;
    const known = ['1/4', '2/4', '3/4', '4/4', '5/4', '6/4', '7/4'];
    setSignature(known.includes(sig) ? sig : 'custom');
    setPattern(Array.from({ length: a }, (_, i) => (i === 0 ? 2 : 1)));
    setPatternB(Array.from({ length: b }, (_, i) => (i === 0 ? 2 : 1)));
  };


  // ===== Presets =====
  const handleSavePreset = () => {
    const name = presetName.trim() || `Preset ${presets.length + 1}`;
    const preset = {
      id: uid(),
      name,
      bpm, signature, pattern: pattern.slice(),
      barsEnabled: barsEnabled.slice(),
      subdivision, soundType, volume,
      mode, sections: JSON.parse(JSON.stringify(sections)), loopSong,
      createdAt: new Date().toISOString(),
    };
    const next = [preset, ...presets];
    setPresets(next);
    savePresets(next);
    setPresetName('');
    toast.success(`Preset "${name}" salvato`);
  };

  const handleLoadPreset = (id) => {
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    setBpm(p.bpm); setBpmInput(String(p.bpm));
    setSignature(p.signature);
    setPattern(p.pattern.slice());
    if (Array.isArray(p.barsEnabled)) setBarsEnabled(p.barsEnabled.slice());
    if (typeof p.volume === 'number') setVolume(p.volume);
    if (p.subdivision) setSubdivision(p.subdivision);
    if (p.soundType) setSoundType(p.soundType);
    if (p.mode) setMode(p.mode);
    if (Array.isArray(p.sections)) setSections(JSON.parse(JSON.stringify(p.sections)));
    if (typeof p.loopSong === 'boolean') setLoopSong(p.loopSong);
    toast.success(`Caricato "${p.name}"`);
  };

  const handleDeletePreset = (id) => {
    const next = presets.filter((p) => p.id !== id);
    setPresets(next);
    savePresets(next);
    toast('Preset eliminato');
  };

  // ===== Share / Import =====
  const buildCurrentPresetData = (overrideName) => ({
    name: overrideName || presetName.trim() || 'Configurazione corrente',
    bpm, signature, pattern: pattern.slice(),
    barsEnabled: barsEnabled.slice(),
    subdivision, soundType, volume,
    mode, sections: JSON.parse(JSON.stringify(sections)), loopSong,
    countInBars, rampEnabled, rampDelta, rampEveryBars, rampMaxBpm,
    patternB: patternB.slice(), soundTypeB,
  });

  const handleShareCurrent = async () => {
    const data = buildCurrentPresetData();
    const url = buildShareUrl(data);
    const summary = data.mode === 'song'
      ? `Song · ${data.sections?.length || 0} sezioni · ${data.bpm} bpm`
      : data.mode === 'polyrhythm'
        ? `Poly ${data.patternB?.length || 0}:${data.pattern.length} · ${data.bpm} bpm`
        : `${data.bpm} bpm · ${data.signature} · ${data.barsEnabled?.length || 1} bar`;
    setShareModal({ url, title: data.name, subtitle: summary });
  };

  const handleSharePreset = (preset) => {
    const url = buildShareUrl(preset);
    const summary = preset.mode === 'song'
      ? `Song · ${preset.sections?.length || 0} sezioni · ${preset.bpm} bpm`
      : preset.mode === 'polyrhythm'
        ? `Poly ${preset.patternB?.length || 0}:${preset.pattern.length} · ${preset.bpm} bpm`
        : `${preset.bpm} bpm · ${preset.signature} · ${preset.barsEnabled?.length || 1} bar`;
    setShareModal({ url, title: preset.name, subtitle: summary });
  };

  const applyImportedPreset = (p) => {
    setBpm(p.bpm); setBpmInput(String(p.bpm));
    setSignature(p.signature);
    setPattern(p.pattern.slice());
    if (Array.isArray(p.barsEnabled)) setBarsEnabled(p.barsEnabled.slice());
    if (typeof p.volume === 'number') setVolume(p.volume);
    if (p.subdivision) setSubdivision(p.subdivision);
    if (p.soundType) setSoundType(p.soundType);
    if (p.mode) setMode(p.mode);
    if (Array.isArray(p.sections)) setSections(JSON.parse(JSON.stringify(p.sections)));
    if (typeof p.loopSong === 'boolean') setLoopSong(p.loopSong);
    if (typeof p.countInBars === 'number') setCountInBars(p.countInBars);
    if (typeof p.rampEnabled === 'boolean') setRampEnabled(p.rampEnabled);
    if (typeof p.rampDelta === 'number') setRampDelta(p.rampDelta);
    if (typeof p.rampEveryBars === 'number') setRampEveryBars(p.rampEveryBars);
    if (typeof p.rampMaxBpm === 'number') setRampMaxBpm(p.rampMaxBpm);
    if (Array.isArray(p.patternB)) setPatternB(p.patternB.slice());
    if (p.soundTypeB) setSoundTypeB(p.soundTypeB);
  };

  const handleImportLoad = () => {
    if (!importedPreset) return;
    applyImportedPreset(importedPreset);
    toast.success(`Caricato "${importedPreset.name}"`);
    setImportedPreset(null);
    clearPresetFromUrl();
  };

  const handleImportSave = () => {
    if (!importedPreset) return;
    applyImportedPreset(importedPreset);
    const newPreset = {
      id: uid(),
      ...importedPreset,
      createdAt: new Date().toISOString(),
    };
    const next = [newPreset, ...presets];
    setPresets(next);
    savePresets(next);
    toast.success(`"${importedPreset.name}" salvato`);
    setImportedPreset(null);
    clearPresetFromUrl();
  };

  const handleImportDismiss = () => {
    setImportedPreset(null);
    clearPresetFromUrl();
  };

  // ===== Derived =====
  const numBars = barsEnabled.length;
  const beatsPerBar = pattern.length;
  const activeBars = barsEnabled.filter(Boolean).length;
  const currentBarEnabled = currentBar >= 0 ? barsEnabled[currentBar] : true;
  const isSongMode = mode === 'song';
  const isPolyMode = mode === 'polyrhythm';

  const statusText = !isPlaying
    ? 'IDLE'
    : inCountIn
      ? `COUNT-IN · ${countInBarsLeft} bar`
      : isSongMode
        ? `RUNNING · SEZ ${currentSection + 1}/${sections.length} · BAR ${currentBar + 1}`
        : isPolyMode
          ? `RUNNING · POLY ${patternB.length}:${pattern.length}`
          : `RUNNING · BAR ${currentBar + 1}/${numBars}`;

  const showRampedBpm = isPlaying && rampEnabled && effectiveBpm > bpm;

  return (
    <div className="relative min-h-screen text-white" data-testid="metronome-root">
      <div className="bg-foam" />
      <div className="bg-grain" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-8 py-4 md:py-8 flex flex-col gap-6 md:gap-8 pb-24 sm:pb-12">
        <TopNav />
        {importedPreset && (
          <ShareImportBanner
            preset={importedPreset}
            onLoad={handleImportLoad}
            onSave={handleImportSave}
            onDismiss={handleImportDismiss}
          />
        )}
        {/* Header */}
        <header className="flex items-end justify-between flex-wrap gap-3 sm:gap-4">
          <div>
            <div className="hidden sm:block text-xs tracking-[0.3em] uppercase text-white/40 mb-1">
              Studio Series · v3.1 · by Maurizio "Pink" Rosa
            </div>
            <h1 className="font-bebas text-3xl sm:text-5xl md:text-6xl tracking-wide leading-none">
              Player<span className="text-pink-400">.</span>
            </h1>
            <p className="hidden sm:block text-sm text-white/50 mt-2 max-w-md">
              Pattern, suoni, subdivisioni, polyritmo, song mode.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs text-white/40 font-mono">
              <span className={`inline-block w-2 h-2 rounded-full ${isPlaying ? (inCountIn ? 'bg-rose-400 animate-pulse' : 'bg-pink-400 shadow-[0_0_8px_rgba(236,72,153,0.8)]') : 'bg-white/20'}`} />
              <span data-testid="status-text">{statusText}</span>
            </div>
            {isPlaying && !inCountIn && mode === 'single' && currentBar >= 0 && (
              <button
                type="button"
                data-testid="jump-to-current-bar-btn"
                onClick={() => {
                  const el = document.querySelector(`[data-testid="bar-toggle-${currentBar}"]`);
                  if (el?.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
                }}
                className="h-8 px-3 rounded-full border border-pink-400/40 hover:border-pink-400/70 hover:bg-pink-500/10 text-pink-300 flex items-center gap-1.5 text-[10px] uppercase tracking-widest transition-all"
                aria-label="Vai alla bar in riproduzione"
                title="Vai alla bar in riproduzione"
              >
                <Crosshair className="w-3 h-3" /> Bar {currentBar + 1}
              </button>
            )}
          </div>
        </header>

        {/* Mode toggle — scrollable horizontally on mobile */}
        <div className="flex gap-1 sm:gap-2 self-start bg-[#111112] border border-white/5 rounded-full p-1 overflow-x-auto no-scrollbar max-w-full">
          <button
            data-testid="mode-single-btn"
            onClick={() => handleModeChange('single')}
            className={`px-3 sm:px-5 py-2 rounded-full text-[11px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] flex items-center gap-2 transition-all whitespace-nowrap shrink-0 ${
              mode === 'single' ? 'bg-pink-400 text-black shadow-[0_0_18px_rgba(236,72,153,0.4)]' : 'text-white/60 hover:text-white'
            }`}
          >
            <Layers className="w-3.5 h-3.5" /> Singola
          </button>
          <button
            data-testid="mode-song-btn"
            onClick={() => handleModeChange('song')}
            className={`px-3 sm:px-5 py-2 rounded-full text-[11px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] flex items-center gap-2 transition-all whitespace-nowrap shrink-0 ${
              mode === 'song' ? 'bg-pink-400 text-black shadow-[0_0_18px_rgba(236,72,153,0.4)]' : 'text-white/60 hover:text-white'
            }`}
          >
            <Music2 className="w-3.5 h-3.5" /> Song
          </button>
          <button
            data-testid="mode-poly-btn"
            onClick={() => handleModeChange('polyrhythm')}
            className={`px-3 sm:px-5 py-2 rounded-full text-[11px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] flex items-center gap-2 transition-all whitespace-nowrap shrink-0 ${
              mode === 'polyrhythm' ? 'bg-amber-400 text-black shadow-[0_0_18px_rgba(251,191,36,0.4)]' : 'text-white/60 hover:text-white'
            }`}
          >
            <Activity className="w-3.5 h-3.5" /> Polyritmo
          </button>
        </div>

        {/* BPM + Transport */}
        <section data-testid="transport-panel" className="bg-[#111112] border border-white/5 rounded-2xl p-6 md:p-8 shadow-2xl">
          {inCountIn && (
            <div data-testid="count-in-banner" className="mb-4 px-4 py-3 rounded-lg bg-rose-500/15 border border-rose-400/40 text-rose-200 font-bebas text-2xl tracking-widest text-center">
              COUNT-IN · {countInBarsLeft} {countInBarsLeft === 1 ? 'BATTUTA' : 'BATTUTE'}
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
            <div>
              <div className="flex items-baseline gap-4 mb-4">
                <Input
                  data-testid="bpm-input"
                  type="number"
                  min={MIN_BPM}
                  max={MAX_BPM}
                  value={bpmInput}
                  onChange={(e) => setBpmInput(e.target.value)}
                  onBlur={handleBpmInputCommit}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  className="font-bebas text-7xl md:text-9xl h-auto bg-transparent border-0 px-0 tracking-tight tabular-nums leading-none w-[6ch] focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-pink-400"
                />
                <div className="flex flex-col justify-end pb-2 md:pb-4">
                  <div className="text-xs tracking-[0.3em] uppercase text-white/50">BPM</div>
                  <div className="text-xs text-white/30 mt-1">{MIN_BPM}–{MAX_BPM}</div>
                  {showRampedBpm && (
                    <div data-testid="effective-bpm" className="mt-1 text-xs text-pink-300 font-mono">
                      ▲ {effectiveBpm}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button data-testid="bpm-decrement-btn" onClick={() => adjustBpm(-1)} className="h-9 w-9 rounded-md border border-white/10 hover:border-white/30 hover:bg-white/5 flex items-center justify-center" aria-label="Decrease BPM">
                  <Minus className="w-4 h-4" />
                </button>
                <Slider data-testid="bpm-slider" value={[bpm]} min={MIN_BPM} max={MAX_BPM} step={1} onValueChange={handleBpmSlider} className="flex-1" />
                <button data-testid="bpm-increment-btn" onClick={() => adjustBpm(1)} className="h-9 w-9 rounded-md border border-white/10 hover:border-white/30 hover:bg-white/5 flex items-center justify-center" aria-label="Increase BPM">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <button
                data-testid="play-stop-btn"
                onClick={togglePlay}
                className={`h-20 rounded-xl flex items-center justify-center gap-3 text-xl font-medium active:scale-[0.98] transition-all ${
                  isPlaying
                    ? 'bg-rose-500 hover:bg-rose-400 text-white shadow-[0_0_30px_rgba(244,63,94,0.4)]'
                    : 'bg-pink-400 hover:bg-pink-300 text-black shadow-[0_0_30px_rgba(236,72,153,0.4)]'
                }`}
              >
                {isPlaying ? <Square className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                <span className="font-bebas tracking-widest text-2xl">{isPlaying ? 'STOP' : 'START'}</span>
              </button>
              <button
                ref={tapBtnRef}
                data-testid="tap-tempo-btn"
                onClick={handleTap}
                className="h-12 rounded-lg border border-white/10 hover:border-white/30 hover:bg-white/5 flex items-center justify-center gap-2 text-sm uppercase tracking-[0.2em]"
              >
                <Hand className="w-4 h-4" /> Tap Tempo
              </button>
            </div>
          </div>
        </section>

        {/* Practice tools: count-in + ramp */}
        <PracticePanel
          countInBars={countInBars}
          onCountInChange={setCountInBars}
          rampEnabled={rampEnabled}
          rampDelta={rampDelta}
          rampEveryBars={rampEveryBars}
          rampMaxBpm={rampMaxBpm}
          onRampToggle={setRampEnabled}
          onRampDeltaChange={setRampDelta}
          onRampEveryBarsChange={setRampEveryBars}
          onRampMaxBpmChange={setRampMaxBpm}
          effectiveBpm={effectiveBpm}
          baseBpm={bpm}
          isPlaying={isPlaying}
          collapsed={practiceCollapsed}
          onCollapseToggle={() => setPracticeCollapsed((c) => !c)}
        />

        {/* Signature + Bars + Volume + Subdivision + Sound */}
        <section data-testid="config-panel" className="bg-[#111112] border border-white/5 rounded-2xl p-6 shadow-xl">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 items-end">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50 block mb-2">Tempo</label>
              <Select value={signature} onValueChange={handleSignatureChange}>
                <SelectTrigger data-testid="signature-select-trigger" className="bg-[#0A0A0A] border-white/10 text-white h-11 font-bebas text-2xl tracking-wide">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111112] border-white/10 text-white max-h-72">
                  {TIME_SIGNATURES.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`signature-option-${s.id}`} className="font-bebas text-lg tracking-wide focus:bg-pink-500/20 focus:text-pink-200">{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50 block mb-2">Subdivisione</label>
              <Select value={String(subdivision)} onValueChange={(v) => setSubdivision(parseInt(v, 10))}>
                <SelectTrigger data-testid="subdivision-select-trigger" className="bg-[#0A0A0A] border-white/10 text-white h-11 font-bebas text-2xl tracking-wide">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111112] border-white/10 text-white">
                  {SUBDIVISIONS.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)} data-testid={`subdivision-option-${s.id}`} className="font-bebas text-base tracking-wide focus:bg-pink-500/20 focus:text-pink-200">
                      <span className="mr-2">{s.symbol}</span>{s.label} · {s.desc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">{kitMode ? 'Kit Drum' : 'Suono'}</label>
                <button
                  data-testid="kit-toggle"
                  type="button"
                  onClick={() => setKitMode((v) => !v)}
                  className={`text-[9px] uppercase tracking-widest h-6 px-2 rounded-full border transition-all ${
                    kitMode
                      ? 'bg-pink-500/15 border-pink-400/60 text-pink-300 shadow-[0_0_10px_rgba(236,72,153,0.3)]'
                      : 'border-white/15 text-white/45 hover:border-white/30 hover:text-white/70'
                  }`}
                >
                  Kit {kitMode ? 'ON' : 'OFF'}
                </button>
              </div>
              {!kitMode ? (
                <Select value={soundType} onValueChange={setSoundType}>
                  <SelectTrigger data-testid="sound-select-trigger" className="bg-[#0A0A0A] border-white/10 text-white h-11 font-bebas text-xl tracking-wide">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111112] border-white/10 text-white">
                    {SOUND_TYPES.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`sound-option-${s.id}`} className="font-bebas text-base tracking-wide focus:bg-pink-500/20 focus:text-pink-200">
                        {s.label} <span className="text-xs text-white/40 ml-1">· {s.desc}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="space-y-2">
                  {/* Kit presets — quick chips */}
                  <div className="flex items-center gap-1 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1">
                    <span className="text-[9px] uppercase tracking-widest text-white/30 mr-1 shrink-0">Preset</span>
                    {KIT_PRESETS.map((p) => {
                      const active = soundClick === p.click && soundBeat === p.beat && soundHigh === p.high;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          data-testid={`kit-preset-${p.id}`}
                          onClick={() => { setSoundClick(p.click); setSoundBeat(p.beat); setSoundHigh(p.high); }}
                          className={`shrink-0 h-7 px-2.5 rounded-full text-[10px] font-mono uppercase tracking-widest transition-all border ${
                            active
                              ? 'bg-pink-400 text-black border-pink-400 shadow-[0_0_10px_rgba(236,72,153,0.4)]'
                              : 'border-white/15 text-white/55 hover:border-pink-400/50 hover:text-pink-200'
                          }`}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                  {[
                    { state: 1, label: 'Click', dot: 'bg-pink-500', value: soundClick, set: setSoundClick, testid: 'kit-click' },
                    { state: 2, label: 'Beat', dot: 'bg-emerald-500', value: soundBeat, set: setSoundBeat, testid: 'kit-beat' },
                    { state: 3, label: 'Acuto', dot: 'bg-sky-500', value: soundHigh, set: setSoundHigh, testid: 'kit-high' },
                  ].map((row) => (
                    <div key={row.state} className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${row.dot} shrink-0`} />
                      <span className="text-[10px] uppercase tracking-widest text-white/45 w-10 shrink-0">{row.label}</span>
                      <Select value={row.value} onValueChange={row.set}>
                        <SelectTrigger data-testid={row.testid} className="bg-[#0A0A0A] border-white/10 text-white h-9 font-bebas text-sm tracking-wide">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#111112] border-white/10 text-white">
                          {SOUND_TYPES.map((s) => (
                            <SelectItem key={s.id} value={s.id} className="font-bebas focus:bg-pink-500/20 focus:text-pink-200">{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!isSongMode && !isPolyMode && (
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50 block mb-2">Battute · max {MAX_BARS}</label>
                <div className="flex items-center gap-2">
                  <button data-testid="bars-decrement-btn" onClick={() => handleNumBarsChange(numBars - 1)} disabled={numBars <= 1} className="h-11 w-11 rounded-md border border-white/10 hover:border-white/30 hover:bg-white/5 flex items-center justify-center disabled:opacity-30" aria-label="Riduci battute">
                    <Minus className="w-4 h-4" />
                  </button>
                  <Input
                    data-testid="bars-count-input"
                    type="number"
                    min={1}
                    max={MAX_BARS}
                    value={numBars}
                    onChange={(e) => handleNumBarsChange(parseInt(e.target.value, 10))}
                    className="h-11 text-center font-bebas text-2xl bg-[#0A0A0A] border-white/10 tabular-nums"
                  />
                  <button data-testid="bars-increment-btn" onClick={() => handleNumBarsChange(numBars + 1)} disabled={numBars >= MAX_BARS} className="h-11 w-11 rounded-md border border-white/10 hover:border-white/30 hover:bg-white/5 flex items-center justify-center disabled:opacity-30" aria-label="Aggiungi battuta">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            <div className={isSongMode ? 'col-span-2 md:col-span-2' : ''}>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50 block mb-2">
                Volume <span className={`ml-2 font-mono tabular-nums ${volume > 1 ? 'text-amber-400' : 'text-white/40'}`}>{Math.round((muted ? 0 : volume) * 100)}%</span>
                {volume > 1 && !muted && <span className="ml-2 text-[9px] text-amber-400 uppercase tracking-widest">Boost</span>}
              </label>
              <div className="flex items-center gap-3 h-11">
                <button data-testid="mute-toggle-btn" onClick={() => setMuted((m) => !m)} className="h-11 w-11 rounded-md border border-white/10 hover:border-white/30 hover:bg-white/5 flex items-center justify-center" aria-label={muted ? 'Riattiva audio' : 'Silenzia'}>
                  {muted ? <VolumeX className="w-4 h-4 text-rose-400" /> : <Volume2 className={`w-4 h-4 ${volume > 1 ? 'text-amber-400' : ''}`} />}
                </button>
                <Slider data-testid="volume-slider" value={[muted ? 0 : volume * 100]} min={0} max={400} step={1} onValueChange={(vals) => { setVolume(vals[0] / 100); if (vals[0] > 0 && muted) setMuted(false); }} />
              </div>
            </div>
          </div>
        </section>

        {/* Pattern editor */}
        <section data-testid="pattern-panel" className="bg-[#111112] border border-white/5 rounded-2xl p-6 md:p-8 shadow-xl">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-white/50">
                Pattern di una battuta · {signature.toUpperCase()}
                {isSongMode && sections[selectedSectionIndex] && (
                  <span className="text-pink-300 normal-case tracking-normal ml-2">
                    [Sezione {selectedSectionIndex + 1}: {sections[selectedSectionIndex].name}]
                  </span>
                )}
              </div>
              <div className="font-bebas text-2xl tracking-wide">
                {beatsPerBar} {beatsPerBar === 1 ? 'quarto' : 'quarti'} disattivabili
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button data-testid="all-beats-on-btn" onClick={allBeatsOn} className="h-9 px-4 rounded-md border border-white/10 hover:border-pink-400/50 hover:text-pink-300 text-xs uppercase tracking-widest">Quarti On</button>
              <button data-testid="all-beats-off-btn" onClick={allBeatsOff} className="h-9 px-4 rounded-md border border-white/10 hover:border-white/30 text-xs uppercase tracking-widest">Quarti Off</button>
              <button data-testid="reset-accents-btn" onClick={resetAccents} className="h-9 px-4 rounded-md border border-white/10 hover:border-white/30 text-xs uppercase tracking-widest flex items-center gap-1.5">
                <RotateCcw className="w-3.5 h-3.5" /> Reset
              </button>
            </div>
          </div>

          <BeatPatternRow
            pattern={pattern}
            currentBeat={currentBeat}
            isPlaying={isPlaying}
            currentBarEnabled={currentBarEnabled}
            onCycleBeat={cycleBeat}
          />

          <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-5 text-xs uppercase tracking-widest text-white/50">
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-pink-500/70 shadow-[0_0_10px_rgba(236,72,153,0.6)]" /> Click</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-emerald-500/80 shadow-[0_0_10px_rgba(16,185,129,0.6)]" /> Beat</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-sky-500/80 shadow-[0_0_10px_rgba(56,189,248,0.6)]" /> Acuto</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm border border-white/20" /> Off</span>
            <span className="ml-auto text-white/40 normal-case tracking-normal">
              Subdivisione attiva: <span className="text-pink-300">{SUBDIVISIONS.find((x) => x.id === subdivision)?.label}</span>
            </span>
          </div>
        </section>

        {/* Bars panel (single mode) OR Song panel OR Polyrhythm panel */}
        {isPolyMode ? (
          <PolyrhythmPanel
            beatsA={pattern.length}
            patternB={patternB}
            soundTypeB={soundTypeB}
            currentBeatB={currentBeatB}
            isPlaying={isPlaying}
            bpm={bpm}
            onSetPolyPreset={handleSetPolyPreset}
            onSetBeatsB={handleSetBeatsB}
            onCycleBeatB={cycleBeatB}
            onSetSoundTypeB={setSoundTypeB}
            trainerEnabled={trainerEnabled}
            trainerPlayBars={trainerPlayBars}
            trainerMuteBars={trainerMuteBars}
            totalBarsPlayed={totalBarsPlayed}
            onTrainerToggle={setTrainerEnabled}
            onTrainerPlayBarsChange={setTrainerPlayBars}
            onTrainerMuteBarsChange={setTrainerMuteBars}
          />
        ) : !isSongMode ? (
          <section data-testid="bars-panel" className="bg-[#111112] border border-white/5 rounded-2xl p-6 md:p-8 shadow-xl">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-white/50">
                  Battute · pulsante luminoso acceso/spento per ciascuna
                </div>
                <div className="font-bebas text-2xl tracking-wide">
                  {numBars} battute · <span className="text-pink-300">{activeBars}</span> attive
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {[16, 32, 64, 128, 500, 1000].map((n) => (
                  <button
                    key={n}
                    data-testid={`bars-quick-${n}-btn`}
                    onClick={() => handleNumBarsChange(n)}
                    className={`h-9 px-3 rounded-md border text-xs uppercase tracking-widest font-mono transition-all ${
                      numBars === n ? 'border-pink-400 bg-pink-500/15 text-pink-200' : 'border-white/10 hover:border-white/30 text-white/70'
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <button data-testid="all-bars-on-btn" onClick={allBarsOn} className="h-9 px-4 rounded-md border border-white/10 hover:border-pink-400/50 hover:text-pink-300 text-xs uppercase tracking-widest flex items-center gap-1.5">
                  <Power className="w-3.5 h-3.5" /> Tutte On
                </button>
                <button data-testid="all-bars-off-btn" onClick={allBarsOff} className="h-9 px-4 rounded-md border border-white/10 hover:border-white/30 text-xs uppercase tracking-widest flex items-center gap-1.5">
                  <PowerOff className="w-3.5 h-3.5" /> Tutte Off
                </button>
              </div>
            </div>

            <div className="max-h-[60vh] overflow-y-auto pr-2 -mr-2" data-testid="bars-scroll">
              <BarGrid barsEnabled={barsEnabled} currentBar={currentBar} isPlaying={isPlaying} onToggleBar={toggleBar} />
            </div>

            <p className="text-xs text-white/40 mt-5 leading-relaxed">
              Ogni battuta ha un pulsante luminoso: tocca per attivare o disattivare la battuta. Le battute disattivate vengono saltate (silenzio). Spazio per Play/Stop.
            </p>
          </section>
        ) : (
          <SongPanel
            sections={sections}
            selectedIndex={selectedSectionIndex}
            currentSection={currentSection}
            isPlaying={isPlaying}
            loopSong={loopSong}
            onSelectSection={selectSection}
            onAddSection={addSection}
            onDuplicateSection={duplicateSection}
            onDeleteSection={deleteSection}
            onMoveSection={moveSection}
            onUpdateSection={updateSection}
            onToggleLoop={setLoopSong}
          />
        )}

        {/* Presets */}
        <section data-testid="presets-panel" className="bg-[#111112] border border-white/5 rounded-2xl p-6 shadow-xl">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-white/50">Presets · localStorage</div>
              <div className="font-bebas text-2xl tracking-wide">Pattern salvati ({presets.length})</div>
            </div>
            <div className="flex items-center gap-2 w-full md:w-auto">
              <Input
                data-testid="preset-name-input"
                placeholder="Nome pattern..."
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSavePreset()}
                className="bg-[#0A0A0A] border-white/10 h-10 w-full md:w-64"
              />
              <button data-testid="save-preset-btn" onClick={handleSavePreset} className="h-10 px-4 rounded-md bg-pink-400 hover:bg-pink-300 text-black font-medium text-sm flex items-center gap-2 active:scale-95 transition-all">
                <Save className="w-4 h-4" /> Salva
              </button>
              <button
                data-testid="share-current-btn"
                onClick={handleShareCurrent}
                className="h-10 px-4 rounded-md border border-white/15 hover:border-pink-400/50 hover:text-pink-300 text-white font-medium text-sm flex items-center gap-2 active:scale-95 transition-all"
                title="Condividi configurazione corrente come link"
              >
                <Share2 className="w-4 h-4" /> Condividi
              </button>
            </div>
          </div>

          {presets.length === 0 ? (
            <div className="text-sm text-white/40 italic py-8 text-center border border-dashed border-white/10 rounded-md">
              Nessun preset salvato. Crea un pattern e premi <span className="text-pink-400">Salva</span>.
            </div>
          ) : (
            <ul data-testid="presets-list" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {presets.map((p) => (
                <li key={p.id} data-testid={`preset-item-${p.id}`} className="group bg-[#0A0A0A] border border-white/5 hover:border-pink-400/40 rounded-md p-3 transition-all">
                  <div className="flex items-start justify-between gap-2">
                    <button data-testid={`preset-load-btn-${p.id}`} onClick={() => handleLoadPreset(p.id)} className="flex-1 text-left">
                      <div className="font-bebas text-xl tracking-wide truncate group-hover:text-pink-300">{p.name}</div>
                      <div className="text-xs text-white/50 font-mono mt-0.5">
                        {p.bpm} bpm · {p.signature?.toUpperCase()} · {p.mode === 'song' ? `${p.sections?.length ?? 0} sez` : `${p.barsEnabled?.length ?? 1} bar`}
                        {p.soundType && ` · ${p.soundType}`}
                      </div>
                    </button>
                    <button data-testid={`preset-delete-btn-${p.id}`} onClick={() => handleDeletePreset(p.id)} className="opacity-50 hover:opacity-100 hover:text-rose-400 p-1.5 rounded transition-all" aria-label={`Elimina preset ${p.name}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button data-testid={`preset-share-btn-${p.id}`} onClick={() => handleSharePreset(p)} className="opacity-50 hover:opacity-100 hover:text-pink-300 p-1.5 rounded transition-all" aria-label={`Condividi preset ${p.name}`}>
                      <Share2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex gap-0.5 mt-2.5">
                    {p.pattern.map((s, i) => (
                      <span key={i} className={`flex-1 h-1.5 rounded-sm ${s === 0 ? 'bg-white/10' : s === 3 ? 'bg-sky-400' : s === 2 ? 'bg-emerald-400' : 'bg-pink-400/70'}`} />
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <footer className="mt-6 mb-4">
          <AuthorSignature />
          <div className="text-center text-[10px] uppercase tracking-[0.4em] text-white/15 mt-4">
            Built with Web Audio · Studio Series v3.2
          </div>
        </footer>
      </div>

      <Toaster position="bottom-right" />
      <InstallPrompt />
      <QRShareModal
        open={!!shareModal}
        url={shareModal?.url}
        title={shareModal?.title}
        subtitle={shareModal?.subtitle}
        onClose={() => setShareModal(null)}
      />

      {/* Sticky mobile bottom transport */}
      <div
        data-testid="mobile-transport-bar"
        className="sm:hidden fixed left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/10 px-4 py-3 flex items-center gap-3"
        style={{ bottom: 'calc(var(--donation-h) + env(safe-area-inset-bottom))' }}
      >
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">{inCountIn ? 'COUNT-IN' : 'BPM'}</div>
          <div className="font-bebas text-2xl leading-none truncate">
            {inCountIn ? `${countInBarsLeft} bar` : `${showRampedBpm ? effectiveBpm : bpm}`}
          </div>
        </div>
        <button
          data-testid="mobile-tap-btn"
          onClick={handleTap}
          className="h-12 w-12 rounded-md border border-white/10 hover:border-white/30 flex items-center justify-center"
          aria-label="Tap tempo"
        >
          <Hand className="w-4 h-4" />
        </button>
        <button
          data-testid="mobile-play-stop-btn"
          onClick={togglePlay}
          className={`h-14 px-6 rounded-full flex items-center justify-center gap-2 font-bebas text-xl tracking-widest active:scale-95 transition-all ${
            isPlaying ? 'bg-rose-500 text-white shadow-[0_0_24px_rgba(244,63,94,0.4)]' : 'bg-pink-400 text-black shadow-[0_0_24px_rgba(236,72,153,0.4)]'
          }`}
        >
          {isPlaying ? <><Square className="w-5 h-5" /> STOP</> : <><Play className="w-5 h-5" /> PLAY</>}
        </button>
      </div>
    </div>
  );
}
